import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Check } from 'lucide-react';
import * as api from '../../lib/api';
import FacilityForm from '../../components/admin/FacilityForm';
import FacilityPhotos from '../../components/admin/FacilityPhotos';

export default function EditFacilityPage() {
  const { facilityId } = useParams();
  const [facility, setFacility] = useState(null);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let live = true;
    setState('loading');
    api.adminFacility(facilityId)
      .then((payload) => { if (live) { setFacility(payload.facility); setState('ready'); } })
      .catch((err) => { if (live) { setError(err.message); setState('error'); } });
    return () => { live = false; };
  }, [facilityId]);

  const submit = async (body) => {
    const payload = await api.adminUpdateFacility(facilityId, body);
    setFacility(payload.facility);
    setSaved(true);
  };

  if (state === 'loading') {
    return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" /></div>;
  }
  if (state === 'error') {
    return <p className="mx-auto mt-8 max-w-md rounded-lg bg-rose-50 p-4 text-sm text-rose-800">{error}</p>;
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <Link to={`/admin/facilities/${facilityId}/units`}
            className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Units
      </Link>
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">{facility.name}</h1>
      <p className="mb-6 mt-1 text-sm text-slate-600">
        Public address: <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">/facilities/{facility.slug}</code>
      </p>
      {/* Photos manage themselves — each action hits the API immediately rather
          than waiting for the form's Save, because an upload has already
          happened by the time you see it and pretending otherwise invites
          someone to navigate away thinking nothing was stored. */}
      <div className="mb-6">
        <FacilityPhotos facilityId={facilityId} initial={facility.photos ?? []} />
      </div>

      <FacilityForm
        mode="edit"
        initial={toForm(facility)}
        onSubmit={submit}
        submitLabel="Save changes"
        footer={saved && (
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
            <Check className="h-4 w-4" aria-hidden="true" /> Saved
          </span>
        )}
      />
    </div>
  );
}

/** Cents to dollars, times to HH:MM, numbers to strings the inputs accept. */
function toForm(f) {
  return {
    ...f,
    lat: String(f.lat ?? ''),
    lng: String(f.lng ?? ''),
    description: f.description ?? '',
    // No editor for amenities yet — carry them through so saving the form
    // does not quietly erase them.
    amenities: f.amenities ?? [],
    address_line2: f.address_line2 ?? '',
    phone: f.phone ?? '',
    email: f.email ?? '',
    gate_opens_at: hhmm(f.gate_opens_at),
    gate_closes_at: hhmm(f.gate_closes_at),
    office_opens_at: hhmm(f.office_opens_at),
    office_closes_at: hhmm(f.office_closes_at),
    admin_fee: dollars(f.admin_fee_cents),
    deposit: dollars(f.deposit_cents),
    late_fee: dollars(f.late_fee_cents),
    tax_rate_pct: String(f.tax_rate_pct ?? '0'),
    late_fee_grace_days: String(f.late_fee_grace_days ?? '5'),
  };
}

const hhmm = (value) => (value ? String(value).slice(0, 5) : '');
const dollars = (cents) => ((cents ?? 0) / 100).toFixed(2);
